// pages/solutions/BrandTeams.jsx
import { useNavigate } from 'react-router-dom';

export default function BrandTeams() {
  const navigate = useNavigate();

  const features = [
    {
      icon: "🎨",
      title: "Brand Consistency",
      description: "Maintain brand guidelines across all campaigns and platforms automatically."
    },
    {
      icon: "⚡",
      title: "Rapid Deployment",
      description: "Launch campaigns in minutes, not weeks, without compromising quality."
    },
    {
      icon: "🌐",
      title: "Multi-Platform",
      description: "Scale your messaging across all major advertising platforms seamlessly."
    },
    {
      icon: "📱",
      title: "Dynamic Creative",
      description: "Automatically adapt creative assets for different formats and sizes."
    },
    {
      icon: "🔒",
      title: "Brand Safety",
      description: "Protect your brand with automated compliance and approval workflows."
    },
    {
      icon: "📊",
      title: "Performance Insights",
      description: "Understand which brand messages resonate most with your audience."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00809D]/20 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Brand Teams
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Scale your brand messaging across platforms without sacrificing quality or waiting weeks for creative updates.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
                Get Started
              </button>
              <button className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all">
                See Examples
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Built for Brand Excellence
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#00809D]/10 to-transparent">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            How It Works
          </h2>
          <div className="space-y-6">
            {[
              { step: "1", title: "Upload Brand Assets", desc: "Define your brand guidelines, colors, fonts, and creative templates" },
              { step: "2", title: "Create Campaigns", desc: "Build campaigns with AI-powered suggestions that match your brand" },
              { step: "3", title: "Deploy Everywhere", desc: "Launch across all platforms with one click, perfectly formatted" },
              { step: "4", title: "Monitor & Optimize", desc: "Track performance and make updates that roll out automatically" }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-6 items-start bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#00809D]/20 to-[#00a0c4]/20 backdrop-blur-lg border border-white/20 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Elevate Your Brand Presence
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join leading brands using our platform to scale their messaging.
          </p>
          <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-10 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
            Start Building
          </button>
        </div>
      </section>
    </div>
  );
}
